import styled from "styled-components";
import { tokens } from "../../public/tokens";
import { MOBILE_BREAKPOINT } from "../library/breakpoints";
import hexToRGBA from "../../utils/hexToRGBA";

const baseListHeaderLabel = tokens.typography.Heading["heading-3"];

const baseDetailHeaderLabel = tokens.typography.Heading["heading-3"];

const baseToolbarButton = {
    height: "35px",
    padding: `0 ${tokens.globals["spacing-2-5"]}`,
    background: tokens.colors.neutral.white,
    boxShadow: tokens.effects.effects.shadow.default,
    borderRadius: tokens.globals["border-radius"],
    border: `1px solid ${tokens.colors.surfaces["border-divider"]}`,
    alignItems: "center",
    justifyContent: "center",
    gap: tokens.globals["spacing-1-5"],
    cursor: "pointer",
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontSize: tokens.typography.fontSize["lg-14px"],
    fontWeight: tokens.typography.fontWeights.semibold,
    color: tokens.colors.text.primary,
};

const baseLockIcon = {
    width: tokens.globals["spacing-4"],
    fontSize: tokens.typography.fontSize["lg-14px"],
    lineHeight: "14px",
    textAlign: "center",
};

export const StyledBlockHeading = styled.div({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    gap: tokens.globals["spacing-md"],
    flexWrap: "wrap",
});

export const StyledHeader = styled.div({
    display: "flex",
    alignItems: "center",
    gap: tokens.globals["spacing-md"],
    flexWrap: "wrap",
    flex: 1,
});

export const StyledTitle = styled.div({
    display: "flex",
    alignItems: "center",
    gap: tokens.globals["spacing-1-5"],
    flexWrap: "wrap",
});

export const StyledListHeadingSpan = styled.span({
    ...baseListHeaderLabel,
    color: tokens.colors.text.primary,
});

export const StyledPmcProgrammeSpan = styled.span({
    ...baseDetailHeaderLabel,
    color: tokens.colors.text.primary,
});

export const StyledProgrammeNameSpan = styled.span({
    ...baseDetailHeaderLabel,
    color: tokens.colors.theme["topbar-color"],
});

export const StyledActions = styled.div({
    display: "flex",
    alignItems: "center",
    gap: tokens.globals["spacing-md"],
    flexWrap: "wrap",
});

export const StyledActionDivider = styled.span({
    width: "1px",
    height: tokens.globals["spacing-9"],
    flexShrink: 0,
    background: tokens.colors.surfaces["border-divider"],
});

export const StyledActionButton = styled.button({
    ...baseToolbarButton,
    display: "flex",
    transition: "all 0.2s",
    textDecoration: "none",

    "&:hover:not(:disabled)": {
        background: tokens.colors.neutral.grey["900"],
        borderColor: tokens.colors.neutral.grey["900"],
        color: tokens.colors.neutral.white,
    },

    "&:disabled": {
        opacity: 0.6,
        cursor: "not-allowed",
    },

    i: {
        fontSize: tokens.typography.fontSize["lg-14px"],
        color: "currentColor",
    },
});

export const StyledViewToggle = styled.div({
    height: tokens.globals["spacing-9"],
    padding: tokens.globals["spacing-1"],
    background: tokens.colors.theme["sidebar-color"],
    borderRadius: tokens.globals["border-radius"],
    boxShadow: tokens.effects.effects.shadow.default,
    display: "inline-flex",
    alignItems: "center",
    gap: 0,
    flexShrink: 0,
});

const activeToggleOptionBackground = hexToRGBA(tokens.colors.neutral.grey["100"], 0.2);

export const StyledViewOption = styled.button(({ $active }) => ({
    height: tokens.globals["spacing-7"],
    padding: `${tokens.globals["spacing-1"]} ${tokens.globals["spacing-2"]}`,
    borderRadius: "8px",
    border: 0,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: $active ? activeToggleOptionBackground : "transparent",
    color: tokens.colors.neutral.white,
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontSize: tokens.typography.fontSize["lg-14px"],
    fontWeight: tokens.typography.fontWeights.semibold,
    lineHeight: "20px",
    whiteSpace: "nowrap",
    transition: "background 0.15s ease-in-out",

    "&:hover": {
        background: $active ? activeToggleOptionBackground : "transparent",
    },

    "&:focus-visible": {
        outline: `1px solid ${tokens.colors.neutral.white}`,
        outlineOffset: "1px",
    },
}));

export const StyledWebsiteSelect = styled.div({
    width: "190px",
});

export const StyledControlsRow = styled.div({
    width: "100%",
    minHeight: "35px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 0,
    marginBottom: tokens.globals["spacing-5"],
    flexWrap: "wrap",
    gap: tokens.globals["spacing-2-5"],

    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
        alignItems: "flex-start",
        flexDirection: "column",
    },
});

export const StyledButtonGroup = styled.div({
    display: "flex",
    alignItems: "center",
    gap: tokens.globals["spacing-2"],
    flexWrap: "wrap",
});

export const StyledToolbarButton = styled.button(({ $tone }) => ({
    height: "35px",
    padding: `0 ${tokens.globals["spacing-4"]}`,
    background:
        $tone === "danger"
            ? tokens.colors.ui.destructive
            : tokens.colors.theme["button-color"],
    boxShadow: tokens.effects.effects.shadow.default,
    borderRadius: tokens.globals["border-radius"],
    border: `1px solid ${
        $tone === "danger"
            ? tokens.colors.ui.destructive
            : tokens.colors.theme["button-color"]
    }`,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: tokens.globals["spacing-1-5"],
    cursor: "pointer",
    color: tokens.colors.neutral.white,
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontSize: tokens.typography.fontSize["lg-14px"],
    fontWeight: tokens.typography.fontWeights.semibold,
    lineHeight: "20px",
    whiteSpace: "nowrap",

    "&:hover:not(:disabled)": {
        background:
            $tone === "danger"
                ? tokens.colors.states.alert["600"]
                : tokens.colors.theme["button-hover-color"],

        borderColor:
            $tone === "danger"
                ? tokens.colors.states.alert["600"]
                : tokens.colors.theme["button-hover-color"],
    },

    "&:focus-visible": {
        outline: `1px solid ${tokens.colors.theme["button-color"]}`,
        outlineOffset: "2px",
    },

    "&:disabled": {
        opacity: 0.6,
        cursor: "not-allowed",
    },

    i: {
        width: tokens.globals["spacing-4"],
        fontSize: tokens.typography.fontSize["lg-14px"],
        lineHeight: "14px",
        textAlign: "center",
    },
}));

export const StyledOutlineButton = styled.button({
    ...baseToolbarButton,
    display: "inline-flex",
    lineHeight: "20px",
    whiteSpace: "nowrap",

    "&:hover:not(:disabled)": {
        background: tokens.colors.neutral.grey["100"],
        borderColor: tokens.colors.neutral.grey["300"],
    },

    "&:focus-visible": {
        outline: `1px solid ${tokens.colors.brand.blue["500"]}`,
        outlineOffset: "2px",
    },

    "&:disabled": {
        opacity: 0.6,
        cursor: "not-allowed",
    },
});

export const StyledLockActionIcon = styled.i({
    ...baseLockIcon,
    color: tokens.colors.states.warning["500"],
});

export const StyledUnlockActionIcon = styled.i({
    ...baseLockIcon,
    color: tokens.colors.neutral.grey["600"],
});
